import { useQuery } from '@tanstack/react-query';

import { fetchROC } from '@/adapters/fetchExaCare';
import {
  getHttpResponseStatus,
  getHttpServerErrorAwareRefetchInterval,
  isHttpServerErrorResponse
} from '@/adapters/query';

import type { ROCReferralDetail } from '../models/ReferralMessageModel';

export const REFERRAL_DETAIL_QUERY_KEY = 'referralDetail';

const POLL_INTERVAL_MS = 5 * 1000;

function shouldStopPolling(error: unknown): boolean {
  const status = getHttpResponseStatus(error);
  return status !== undefined && status >= 400 && status < 500;
}

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (shouldStopPolling(error) || isHttpServerErrorResponse(error)) {
    return false;
  }
  return failureCount < 3;
}

export function useReferralDetailQuery(referralId: string) {
  return useQuery({
    queryKey: [REFERRAL_DETAIL_QUERY_KEY, referralId],
    queryFn: () => fetchROC.get<ROCReferralDetail>(`/referrals/${referralId}`),
    staleTime: 0,
    refetchInterval: (_data, query) =>
      shouldStopPolling(query.state.error)
        ? false
        : getHttpServerErrorAwareRefetchInterval(POLL_INTERVAL_MS, query),
    refetchIntervalInBackground: false,
    enabled: !!referralId,
    retryOnMount: false,
    retry: shouldRetry
  });
}
